import { Slot, Slottable } from '@radix-ui/react-slot';
import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { cn } from './cn';
import { Tooltip } from './tooltip';

/**
 * action — the everyday primary (asphalt by day, lane white by night).
 * brand — Waypoint Red: one per screen at most, for the step that moves the work forward.
 * secondary · ghost (red text, a link) · quiet (neutral, for toolbars and icon buttons) · danger (hazard).
 * Caution amber is never a button.
 */
export type ButtonVariant = 'action' | 'brand' | 'secondary' | 'ghost' | 'quiet' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  action: 'bg-action text-on-action hover:bg-action-hover active:bg-action-pressed',
  brand: 'bg-brand text-on-brand hover:bg-brand-hover active:bg-brand-pressed',
  secondary: 'border-line-strong bg-surface text-ink hover:border-line-input hover:bg-hover',
  ghost: 'bg-transparent text-link hover:bg-hover hover:text-link-hover',
  quiet: 'bg-transparent text-ink-muted hover:bg-hover hover:text-ink active:bg-pressed',
  danger: 'bg-hazard-fill text-hazard-on-fill hover:brightness-[0.94]',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-control-sm px-3 text-[13px] leading-[18px]',
  md: 'h-control-md px-[18px] text-button',
  lg: 'h-control-lg px-6 text-button',
};

const SQUARE: Record<ButtonSize, string> = {
  sm: 'w-[var(--vlm-control-sm)] px-0',
  md: 'w-[var(--vlm-control-md)] px-0',
  lg: 'w-[var(--vlm-control-lg)] px-0',
};

/** The button look, for elements that are not <Button> (a router link, a label). */
export function buttonClass({ variant = 'action', size = 'md', square = false }: { variant?: ButtonVariant; size?: ButtonSize; square?: boolean } = {}) {
  return cn(
    'relative inline-flex shrink-0 select-none items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-md border border-transparent font-sans font-bold no-underline',
    'transition-[background-color,border-color,color,filter] duration-quick ease-drive active:translate-y-px',
    'disabled:cursor-not-allowed disabled:border-line disabled:bg-surface-sunken disabled:text-ink-disabled disabled:active:translate-y-0',
    SIZES[size],
    square && SQUARE[size],
    VARIANTS[variant],
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** An icon before the label (lucide-react at 16–18 px). */
  icon?: ReactNode;
  /** Busy: the lane flows along the bottom edge and clicks are ignored; the look stays. */
  loading?: boolean;
  /** Render the child element (a router link) with the button look. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, icon, loading = false, asChild = false, className, children, type, onClick, ...rest },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  const guarded = loading ? (event: MouseEvent<HTMLButtonElement>) => event.preventDefault() : onClick;
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(buttonClass({ variant, size }), loading && 'cursor-progress', className)}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={guarded}
      {...rest}
    >
      {icon}
      <Slottable>{children}</Slottable>
      {loading && <span className="vlm-btn-lane" aria-hidden="true" />}
    </Comp>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'children' | 'asChild'> {
  /** The accessible name, also shown as the tooltip. */
  label: string;
  icon: ReactNode;
  /** A badge over the icon (e.g. the unread count). */
  badge?: ReactNode;
  tooltip?: boolean;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, badge, tooltip = true, tooltipSide = 'bottom', variant = 'quiet', size = 'md', className, ...rest },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      className={cn(buttonClass({ variant, size, square: true }), badge !== undefined && 'overflow-visible', className)}
      {...rest}
    >
      {icon}
      {badge}
    </button>
  );
  return tooltip ? (
    <Tooltip label={label} side={tooltipSide}>
      {button}
    </Tooltip>
  ) : (
    button
  );
});
